import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Alert, 
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  LinearProgress
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, writeBatch, getDoc, collection, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { alpha } from '@mui/material/styles';

const CategoryBulkUpdate = ({ categories, open, onClose }) => {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '', isProcessing: false });

  // Helper function to get category value
  const getCategoryValue = (hotel, fieldName) => {
    const categoryKey = `category_${fieldName.toLowerCase()}`;
    return hotel.categoryData?.[categoryKey]?.value || '';
  };

  // Helper function to validate property number
  const validatePropertyNumber = (propertyNumber) => {
    if (!propertyNumber) return false;
    // Remove any whitespace
    propertyNumber = propertyNumber.toString().trim();
    // Check if it's empty after trimming
    if (propertyNumber === '') return false;
    return true;
  };

  // Helper function to validate category value based on type
  const validateCategoryValue = (value, type) => {
    if (value === undefined || value === null || value === '') return true; // Empty values are allowed
    
    // Clean the value by removing any whitespace, carriage returns, or line feeds
    const cleanValue = String(value).trim().replace(/[\r\n]+/g, '');
    
    switch (type) {
      case 'number':
        return !isNaN(cleanValue);
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanValue);
      case 'url':
        try {
          new URL(cleanValue);
          return true;
        } catch {
          return false;
        }
      case 'telephone':
        // Telephone format validation - must start with "+" followed by numbers
        // We're being flexible with the format (no specific country code requirements)
        return /^\+[0-9]{6,15}$/.test(cleanValue.replace(/[\s-]/g, ''));
      default:
        return true;
    }
  };

  // Generate template spreadsheet
  const generateTemplate = async () => {
    try {
      if (!Array.isArray(categories) || categories.length === 0) {
        throw new Error('No categories available');
      }

      console.log('Starting template generation...');
      console.log('Categories received:', categories);
      
      // Get all hotels
      const hotelsRef = collection(db, 'hotels');
      console.log('Fetching hotels from:', hotelsRef.path);
      const hotelsSnapshot = await getDocs(hotelsRef);
      console.log('Found hotels:', hotelsSnapshot.size);

      if (hotelsSnapshot.empty) {
        throw new Error('No hotels found in the database');
      }

      // Prepare category structure (excluding propertyNumber)
      const categoryFields = categories.flatMap(group => 
        group.items
          .filter(item => !item.isArchived && item.fieldName !== 'propertyNumber')
          .map(item => ({
            title: item.title,
            fieldName: item.fieldName,
            type: item.type
          }))
      );

      if (categoryFields.length === 0) {
        throw new Error('No active categories found');
      }

      console.log('Category fields:', categoryFields);

      // Create the data array with headers
      const data = [
        // Headers row
        ['Property Number', ...categoryFields.map(field => field.title)]
      ];
      console.log('Headers:', data[0]);

      // Add hotel data
      hotelsSnapshot.forEach(doc => {
        const hotel = doc.data();
        if (!hotel) {
          console.warn(`Empty hotel document found: ${doc.id}`);
          return;
        }

        // Skip archived hotels (check both status and isArchived field)
        if (hotel.status === 'archived' || hotel.isArchived || hotel.categoryData?.isArchived?.value === true) {
          console.log(`Skipping archived hotel: ${doc.id}`);
          return;
        }

        console.log('Processing hotel:', {
          id: doc.id,
          propertyNumber: hotel.propertyNumber,
          categoryData: hotel.categoryData
        });
        
        const row = [
          hotel.propertyNumber || doc.id, // Use document ID as fallback
          ...categoryFields.map(field => getCategoryValue(hotel, field.fieldName))
        ];
        console.log('Generated row:', row);
        data.push(row);
      });

      if (data.length <= 1) {
        throw new Error('No valid hotel data found');
      }

      console.log('Total rows:', data.length);

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(data);

      // Add column widths and styling
      const colWidths = [
        { wch: 15 }, // Property Number column
        ...categoryFields.map(() => ({ wch: 20 })) // Other columns
      ];
      ws['!cols'] = colWidths;

      // Add the worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Categories');

      // Save the file
      XLSX.writeFile(wb, 'hotel_categories_template.xlsx');
      console.log('Template generated successfully');
    } catch (error) {
      console.error('Error generating template:', error);
      setError('Failed to generate template: ' + error.message);
    }
  };

  // Handle file upload and processing
  const handleFileUpload = async (event) => {
    try {
      console.log('Starting file upload...');
      setUploading(true);
      setError(null);
      setSuccess(null);
      setProgress({ current: 0, total: 0, message: 'Reading file...', isProcessing: true });

      const file = event.target.files[0];
      if (!file) {
        console.log('No file selected');
        return;
      }
      console.log('File selected:', file.name);

      // Validate file type
      const fileType = file.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls'].includes(fileType)) {
        throw new Error('Invalid file type. Please upload an Excel file (.xlsx or .xls)');
      }

      // Read the spreadsheet
      const reader = new FileReader();
      
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        setError('Error reading file: ' + error.message);
      };

      reader.onload = async (e) => {
        try {
          console.log('File read successfully');
          setProgress(prev => ({ ...prev, message: 'Processing spreadsheet...', isProcessing: true }));
          
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          console.log('Workbook loaded:', workbook.SheetNames);
          
          // Get the first worksheet
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(worksheet);
          console.log('Rows parsed:', rows.length);

          if (rows.length === 0) {
            throw new Error('No data found in spreadsheet');
          }

          // Set total for progress bar
          setProgress(prev => ({ ...prev, total: rows.length }));

          // Validate headers
          const requiredHeader = 'Property Number';
          if (!rows[0].hasOwnProperty(requiredHeader)) {
            throw new Error(`Missing required column: ${requiredHeader}`);
          }

          // Prepare category field mapping
          const categoryFields = categories.flatMap(group => 
            group.items
              .filter(item => !item.isArchived && item.fieldName !== 'propertyNumber')
              .map(item => ({
                title: item.title,
                fieldName: item.fieldName,
                type: item.type
              }))
          );
          console.log('Category fields prepared:', categoryFields);

          // Start a batch write
          const batch = writeBatch(db);
          let updatedCount = 0;
          let unchangedCount = 0;
          const errors = [];
          const processedPropertyNumbers = new Set(); // Track duplicates

          // Process each row
          console.log('Starting to process rows...');
          for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const propertyNumber = String(row[requiredHeader] || '').trim();
            
            // Update progress
            setProgress(prev => ({ 
              ...prev, 
              current: i + 1, 
              message: `Processing hotel ${propertyNumber} (${i + 1}/${rows.length})...` 
            }));

            // Validate property number
            if (!validatePropertyNumber(propertyNumber)) {
              console.log('Invalid property number:', propertyNumber);
              errors.push(`Skipped row: Invalid Property Number`);
              continue;
            }

            // Check for duplicates in the spreadsheet
            if (processedPropertyNumbers.has(propertyNumber)) {
              console.log('Duplicate property number found:', propertyNumber);
              errors.push(`Skipped duplicate Property Number: ${propertyNumber}`);
              continue;
            }
            processedPropertyNumbers.add(propertyNumber);

            try {
              // Get the hotel document
              const hotelRef = doc(db, 'hotels', propertyNumber);
              const hotelDoc = await getDoc(hotelRef);
              
              if (!hotelDoc.exists()) {
                console.log('Hotel not found:', propertyNumber);
                errors.push(`Skipped hotel ${propertyNumber}: Not found`);
                continue;
              }

              // Get current hotel data
              const currentHotel = hotelDoc.data();
              if (!currentHotel) {
                console.log('Invalid hotel data:', propertyNumber);
                errors.push(`Skipped hotel ${propertyNumber}: Invalid data`);
                continue;
              }

              // Prepare category updates and check for changes
              const categoryData = {};
              let hasChanges = false;
              let validationErrors = [];

              categoryFields.forEach(field => {
                const newValue = row[field.title];
                if (newValue !== undefined && newValue !== null && newValue !== '') {
                  console.log(`Processing field ${field.title} for hotel ${propertyNumber}:`, newValue);
                  
                  // Clean the value before validation and storage
                  let cleanValue = String(newValue).trim().replace(/[\r\n]+/g, '');
                  
                  // Special handling for telephone numbers
                  if (field.type === 'telephone') {
                    // Ensure it starts with "+"
                    if (cleanValue && !cleanValue.startsWith('+')) {
                      cleanValue = `+${cleanValue}`;
                    }
                  }
                  
                  // Validate the cleaned value based on field type
                  if (!validateCategoryValue(cleanValue, field.type)) {
                    validationErrors.push(`Invalid ${field.type} value for ${field.title}: ${cleanValue}`);
                    return;
                  }

                  const categoryKey = `category_${field.fieldName.toLowerCase()}`;
                  const currentValue = currentHotel.categoryData?.[categoryKey]?.value;
                  
                  // Compare cleaned values
                  if (cleanValue !== String(currentValue).trim()) {
                    hasChanges = true;
                    categoryData[categoryKey] = {
                      value: cleanValue,
                      updatedAt: new Date(),
                      updatedBy: user.email
                    };
                    console.log(`Value changed for ${field.title}:`, { old: currentValue, new: cleanValue });
                  }
                }
              });

              // If there are validation errors, skip this hotel
              if (validationErrors.length > 0) {
                console.log('Validation errors for hotel:', propertyNumber, validationErrors);
                errors.push(`Skipped hotel ${propertyNumber} due to validation errors:\n${validationErrors.join('\n')}`);
                continue;
              }

              if (hasChanges) {
                // Update hotel document only if there are changes
                console.log(`Updating hotel ${propertyNumber} with changes:`, categoryData);
                batch.update(hotelRef, {
                  categoryData: {
                    ...currentHotel.categoryData,
                    ...categoryData
                  },
                  lastUpdated: new Date(),
                  lastUpdatedBy: user.email
                });
                updatedCount++;
              } else {
                console.log(`No changes needed for hotel ${propertyNumber}`);
                unchangedCount++;
              }
            } catch (err) {
              console.error(`Error processing hotel ${propertyNumber}:`, err);
              errors.push(`Error processing hotel ${propertyNumber}: ${err.message}`);
            }
          }

          // Commit the batch only if there are updates
          if (updatedCount > 0) {
            setProgress(prev => ({ ...prev, message: 'Saving changes...' }));
            try {
              await batch.commit();
              console.log('Batch committed successfully');
            } catch (err) {
              console.error('Error committing batch:', err);
              throw new Error(`Failed to save changes: ${err.message}`);
            }
          }

          // Show success message
          setProgress({ current: 0, total: 0, message: '' });
          let message = updatedCount > 0 
            ? `Successfully updated ${updatedCount} hotels`
            : 'No changes were needed';
            
          if (unchangedCount > 0) {
            message += `\n${unchangedCount} hotels were unchanged`;
          }
          if (errors.length > 0) {
            message += `\nWarnings:\n${errors.join('\n')}`;
          }
          console.log('Final status:', message);
          setSuccess(message);
          
          // Reset file input
          event.target.value = '';

        } catch (error) {
          console.error('Error processing spreadsheet:', error);
          setError(error.message);
        } finally {
          setProgress(prev => ({ ...prev, isProcessing: false }));
        }
      };

      reader.readAsArrayBuffer(file);

    } catch (error) {
      console.error('Error handling file upload:', error);
      setError(error.message);
    } finally {
      setUploading(false);
      setProgress(prev => ({ ...prev, current: 0, total: 0, message: '', isProcessing: false }));
    }
  };

  // Render component only if user is admin
  if (!user?.role === 'admin') {
    return (
      <Box p={3}>
        <Typography color="error">
          You don't have permission to access this feature.
        </Typography>
      </Box>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h6">Bulk Update Categories</Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Download the template spreadsheet, fill in the category values for each property number,
          then upload the completed file to update multiple hotels at once.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={generateTemplate}
            sx={{ borderRadius: 2 }}
          >
            Download Template
          </Button>

          <Button
            variant="contained"
            component="label"
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadFileIcon />}
            disabled={uploading}
            sx={{ borderRadius: 2 }}
          >
            Upload Spreadsheet
            <input
              type="file"
              hidden
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              disabled={uploading}
            />
          </Button>
        </Box>

        {(uploading || progress.isProcessing) && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              mb: 3, 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              bgcolor: theme => alpha(theme.palette.background.paper, 0.6)
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Upload Progress
            </Typography>
            
            {progress.total > 0 ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {progress.message}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {Math.round((progress.current / progress.total) * 100)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(progress.current / progress.total) * 100}
                  sx={{ 
                    height: 8,
                    borderRadius: 4,
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4
                    }
                  }}
                />
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  {progress.message || 'Processing...'}
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Typography sx={{ mt: 2 }} variant="body2">
          The spreadsheet will include all active categories from your system. Fill in the values and upload it back to update your hotels.
          Values will be validated based on their field types (e.g., email, URL, number).
          <Box component="span" sx={{ display: 'block', mt: 1, color: 'primary.main' }}>
            Note: Phone numbers should be entered with country codes (e.g., +1234567890). The "+" will be added automatically if missing.
          </Box>
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryBulkUpdate; 